import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Drawer,
  TextField,
  Button,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Typography
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    width: '40%',
    padding: 28,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 16,
    borderBottom: '1px solid #E0E0E0',
    height: '60px'
  },
  title: { fontWeight: 700, fontSize: 18 },
  content: { flex: 1, overflowY: 'auto', marginBottom: 24 },
  sectionBox: {
    background: '#FAFAFA',
    padding: 16,
    marginBottom: 16
  },
  sectionBoxAlt: {
    borderRadius: 8,
    border: '1px solid #C3DDEE',
    background: '#E9F4FA',
    padding: 16,
    marginBottom: 16,
  },
  formRow: { display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap' },
  fieldInput: { flex: '1 1 0', maxWidth: '100%' },
  labelSmall: { fontSize: 14, color: '#757575', marginBottom: 8 },
  labelMedium: { fontSize: 14, color: '#1E293B', fontWeight: 500, marginBottom: 8 },
  smallButton: {
    backgroundColor: '#00445D',
    color: '#fff',
    textTransform: 'none',
    borderRadius: 24,
    padding: '6px 12px',
    '&:hover': { backgroundColor: '#0d1b5e' }
  },
  chipBox: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 12,
    paddingBottom: 8
  },
  extractedFieldChip: {
    backgroundColor: '#E9F4FA',
    color: '#00445D',
    '& .MuiChip-deleteIcon': {
      color: '#00445D',
      '&:hover': { color: '#0d1b5e' }
    }
  },
  clearButton: { textTransform: 'none' },
  footer: {
    borderTop: '1px solid #E0E0E0',
    paddingTop: 16,
    display: 'flex',
    justifyContent: 'flex-end',
    height: '6%'
  },
  helperText: {
    fontSize: 12,
    color: '#757575',
    marginTop: 4
  }
}));

export default function AddFieldModal({ 
  open, 
  onClose, 
  onCreate, 
  onSave, 
  initialData = null, 
  isEdit = false,
  onAddExtractedField,
  onRemoveExtractedField
}) {
  const classes = useStyles();
  const [name, setName] = React.useState('');
  const [type, setType] = React.useState('');
  const [searchable, setSearchable] = React.useState(true);
  const [autoPopulate, setAutoPopulate] = React.useState(false);
  const [newValue, setNewValue] = React.useState('');
  const [acceptableValues, setAcceptableValues] = React.useState([]);
  
  // Extracted fields (aliases)
  const [extractedFields, setExtractedFields] = React.useState([]);
  const [newExtractedField, setNewExtractedField] = React.useState('');

  // Error states
  const [nameError, setNameError] = React.useState(false);
  const [typeError, setTypeError] = React.useState(false);
  const [extractedFieldsError, setExtractedFieldsError] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setName('');
      setType('');
      setSearchable(true);
      setAutoPopulate(false);
      setNewValue('');
      setAcceptableValues([]);
      setExtractedFields([]);
      setNewExtractedField('');
      setNameError(false);
      setTypeError(false);
      setExtractedFieldsError(false);
    } else if (initialData) {
      setName(initialData.name || '');
      const itype = (initialData.type || '').toString();
      setType(itype.toLowerCase() === 'timestamp' ? 'Timestamp' : 'String');
      setSearchable(typeof initialData.searchable === 'boolean' ? initialData.searchable : 
                    typeof initialData.isSearchable === 'boolean' ? initialData.isSearchable : true);
      setAutoPopulate(typeof initialData.autoPopulate === 'boolean' ? initialData.autoPopulate : 
                      typeof initialData.isAutoPopulate === 'boolean' ? initialData.isAutoPopulate : true);
      if (initialData.acceptableValues || initialData.acceptedValues) {
        const vals = initialData.acceptableValues || initialData.acceptedValues;
        if (Array.isArray(vals)) setAcceptableValues(vals);
        else if (typeof vals === 'string' && vals.trim() !== '')
          setAcceptableValues(vals.split(',').map(s => s.trim()));
      }
      // Set extracted fields (aliases) if available
      if (initialData.aliases && Array.isArray(initialData.aliases)) {
        setExtractedFields(initialData.aliases.map(a => ({ id: a.id, name: a.name })));
      } else {
        setExtractedFields([]);
      }
    }
  }, [open, initialData]);

  React.useEffect(() => {
    if (!isEdit && autoPopulate && acceptableValues.length > 0) {
      setAcceptableValues([]);
    }
  }, [autoPopulate, isEdit]);

  const handleAddValue = () => {
    const v = (newValue || '').trim();
    if (!v) return;
    if (!acceptableValues.includes(v)) setAcceptableValues(prev => [...prev, v]);
    setNewValue('');
  };

  const handleDeleteValue = (val) => setAcceptableValues(prev => prev.filter(v => v !== val));
  const handleClearValues = () => setAcceptableValues([]);

  // Extracted fields handlers
  const handleAddExtractedField = () => {
    const v = (newExtractedField || '').trim();
    if (!v) return;
    if (!extractedFields.some(f => f.name === v)) {
      const newField = { id: null, name: v, isNew: true };
      setExtractedFields(prev => [...prev, newField]);
    }
    setNewExtractedField('');
  };

  const handleDeleteExtractedField = (fieldToDelete) => {
    setExtractedFields(prev => prev.filter(f => f.name !== fieldToDelete.name));
  };

  const validateForm = () => {
    const validName = !!name.trim();
    const validType = !!type;
    const validExtractedFields = autoPopulate ? extractedFields.length > 0 : true;

    setNameError(!validName);
    setTypeError(!validType);
    setExtractedFieldsError(autoPopulate && !validExtractedFields);

    return validName && validType && validExtractedFields;
  };

  const handleCreate = () => {
    if (!validateForm()) return;

    const payload = {
      name: name.trim(),
      type: type.toLowerCase(),
      searchable,
      autoPopulate,
      acceptedValues: autoPopulate ? [] : acceptableValues,
      extractedFields: extractedFields.map(f => f.name)
    };

    if (isEdit) {
      payload.id = initialData?.id;
      onSave?.(payload);
    } else {
      onCreate?.(payload);
    }
    onClose?.();
  };

  const isButtonDisabled = !name.trim() || !type || 
    (autoPopulate && extractedFields.length === 0);

  return (
    <Drawer anchor="right" open={!!open} onClose={onClose} classes={{ paper: classes.drawerPaper }}>
      <Box className={classes.header}>
        <Typography className={classes.title}>{isEdit ? 'Edit Field' : '+ Add Field'}</Typography>
        <IconButton size="small" onClick={onClose} aria-label="close"><CloseIcon /></IconButton>
      </Box>

      <Box className={classes.content}>
        {/* Field Info */}
        <Box className={classes.sectionBox}>
          <Typography className={classes.labelSmall}>Field Info</Typography>
          <Box className={classes.formRow}>
            <Box className={classes.fieldInput}>
              <TextField
                label={<span>Field Name <span style={{ color: 'red' }}>*</span></span>}
                fullWidth
                value={name}
                onChange={e => setName(e.target.value)}
                error={nameError}
                helperText={nameError ? 'Field Name is required' : 'Display name shown in the UI'}
                disabled={!!isEdit}
              />
            </Box>
            <Box className={classes.fieldInput}>
              <FormControl fullWidth error={typeError}>
                <InputLabel id="field-type-label">
                  <span>Field Type <span style={{ color: 'red' }}>*</span></span>
                </InputLabel>
                <Select
                  labelId="field-type-label"
                  value={type}
                  onChange={e => setType(e.target.value)}
                  disabled={!!isEdit}
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    getContentAnchorEl: null
                  }}
                >
                  <MenuItem value="String">String</MenuItem>
                  <MenuItem value="Timestamp">Timestamp</MenuItem>
                </Select>
                {typeError && <Typography variant="caption" color="error">Field Type is required</Typography>}
              </FormControl>
            </Box>
          </Box>
        </Box>

        {/* Options */}
        <Box className={classes.sectionBox}>
          <Typography className={classes.labelSmall}>Options</Typography>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <FormControlLabel
              control={<Checkbox checked={searchable} onChange={e => setSearchable(e.target.checked)} color="primary" />}
              label="Make Field Searchable"
            />
            <FormControlLabel
              control={<Checkbox checked={autoPopulate} onChange={e => setAutoPopulate(e.target.checked)} color="primary" disabled={!!isEdit} />}
              label="Auto Populate Field"
            />
          </Box>
        </Box>

        {/* Extracted Fields (Aliases) - Only show when Auto Populate is ON */}
        {autoPopulate && (
          <Box className={classes.sectionBoxAlt}>
            <Typography className={classes.labelMedium}>
              Extracted Fields <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Typography className={classes.helperText} style={{ marginBottom: 12 }}>
              Add the exact field names as they appear on the document. These names will be used to automatically extract values.
            </Typography>
            
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Add extraction field name"
                value={newExtractedField}
                onChange={e => setNewExtractedField(e.target.value)}
                onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddExtractedField(); } }}
                fullWidth
              />
              <Button
                className={classes.smallButton}
                onClick={handleAddExtractedField}
                style={{ height: 40, borderRadius: 20, padding: '0 24px', whiteSpace: 'nowrap' }}
              >
                Add
              </Button>
            </Box>

            {extractedFieldsError && (
              <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>
                At least one extracted field is required for auto-populate
              </Typography>
            )}

            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
              <Typography variant="subtitle2">Extraction Names ({extractedFields.length})</Typography>
              {extractedFields.length > 0 && (
                <Button color="primary" className={classes.clearButton} onClick={() => setExtractedFields([])}>
                  Clear All
                </Button>
              )}
            </Box>

            <Box className={classes.chipBox}>
              {extractedFields.map((field, idx) => (
                <Chip 
                  key={field.id || `new-${idx}`} 
                  label={field.name} 
                  className={classes.extractedFieldChip}
                  onDelete={() => handleDeleteExtractedField(field)} 
                />
              ))}
              {extractedFields.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No extraction fields added yet
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Acceptable Values - Always show for new fields, independent of Auto Populate */}
        {!isEdit && (
          <Box className={classes.sectionBox}>
            <Typography className={classes.labelMedium}>Acceptable Values (Optional)</Typography>
            <Typography className={classes.helperText} style={{ marginBottom: 12 }}>
              Restrict this field to only accept specific values. Leave empty to allow any value.
            </Typography>
            
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Add acceptable value"
                value={newValue}
                onChange={e => setNewValue(e.target.value)}
                onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddValue(); } }}
                fullWidth
              />
              <Button
                className={classes.smallButton}
                onClick={handleAddValue}
                style={{ height: 40, borderRadius: 20, padding: '0 24px', whiteSpace: 'nowrap' }}
              >
                Add
              </Button>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
              <Typography variant="subtitle2">Values ({acceptableValues.length})</Typography>
              {acceptableValues.length > 0 && (
                <Button color="primary" className={classes.clearButton} onClick={handleClearValues}>
                  Clear All
                </Button>
              )}
            </Box>

            <Box className={classes.chipBox}>
              {acceptableValues.map(v => (
                <Chip key={v} label={v} variant="outlined" onDelete={() => handleDeleteValue(v)} />
              ))}
              {acceptableValues.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No restrictions - any value allowed
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Box className={classes.footer}>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={isButtonDisabled}
          className={classes.smallButton}
        >
          {isEdit ? 'Save Changes' : 'Create Field'}
        </Button>
      </Box>
    </Drawer>
  );
}
